import { useState } from "react";
import { AlertTriangle, FileText, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { ChannelIcon } from "@/components/crm/ChannelIcon";
import { brand } from "@/lib/brand";

const isImage = (u) => /\.(png|jpe?g|gif|webp|heic)(\?|$)/i.test(u);

const Attachment = ({ url, out }) => (isImage(url) ? (
  <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt="Adjunto" className="mt-2 max-h-56 rounded-xl object-cover" /></a>
) : (
  <a href={url} target="_blank" rel="noopener noreferrer" data-testid="message-attachment"
     className={`mt-2 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs underline ${out ? "bg-white/15" : "bg-[#F5F7FA]"}`}>
    <FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Ver adjunto</span>
  </a>
));

export const MessageBubble = ({ m, conversationId, onRetried }) => {
  const [retrying, setRetrying] = useState(false);
  const out = m.direction === "outbound";
  const failed = m.delivery_status === "no_enviado";
  const retry = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/conversations/${conversationId}/messages/${m.id}/retry`);
      data.delivery_status === "enviado" ? toast.success("Mensaje enviado") : toast.error(brand(data.error_reason) || "No enviado");
      onRetried?.();
    } catch (e) { toast.error(errorMessage(e)); } finally { setRetrying(false); }
  };
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`thread-message-${m.direction}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm sm:max-w-[70%] ${failed ? "rounded-br-md border border-rose-200 bg-rose-50 text-rose-900" : out ? "rounded-br-md bg-[#1E6FD9] text-white" : "rounded-bl-md border border-slate-200 bg-white"}`}>
        {m.subject && <p className="mb-1 text-xs font-semibold opacity-80">{m.subject}</p>}
        <p className="whitespace-pre-wrap break-words">{m.body}</p>
        {(m.attachments || []).map((u) => <Attachment key={u} url={u} out={out && !failed} />)}
        <p className={`mt-1 flex flex-wrap items-center gap-1 text-[11px] ${failed ? "text-rose-600" : out ? "text-white/70" : "text-slate-400"}`}>
          <ChannelIcon channel={m.channel} className="h-3 w-3" /> {brand(m.author_name)} · {fmtDateTime(m.sent_at)}
          {out && m.delivery_status === "enviando" && <span>· Enviando…</span>}
        </p>
        {failed && (
          <div className="mt-2 border-t border-rose-200 pt-2 text-xs" data-testid="message-not-sent">
            <p className="flex items-start gap-1 font-semibold"><AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> No enviado</p>
            <p className="mt-0.5" data-testid="message-error-reason">{brand(m.error_reason)}</p>
            {conversationId && (
              <button onClick={retry} disabled={retrying} className="mt-1.5 inline-flex items-center gap-1 font-semibold text-[#1E6FD9]" data-testid={`message-retry-${m.id}`}>
                {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />} Reintentar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const Avatar = ({ name, url, size = "h-10 w-10" }) => (url ? (
  <img src={url} alt={name} className={`${size} shrink-0 rounded-full object-cover`} />
) : (
  <span className={`${size} grid shrink-0 place-items-center rounded-full bg-[#0B2545] text-sm font-bold text-white`}>
    {(name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
  </span>
));
